import { cn } from '@ludus/ui'
import { formatMinute, minuteToY } from './timeMath.ts'

export type BlockKind = 'template' | 'exception' | 'preview' | 'off'

interface ScheduleBlockProps {
  kind: BlockKind
  startMinute: number
  endMinute: number
  color?: string
  label?: string
  onClick?: () => void
}

function hexToRgba(hex: string, alpha: number): string {
  const cleaned = hex.replace('#', '')
  const r = parseInt(cleaned.substring(0, 2), 16)
  const g = parseInt(cleaned.substring(2, 4), 16)
  const b = parseInt(cleaned.substring(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export function ScheduleBlock({
  kind,
  startMinute,
  endMinute,
  color = '#6366f1',
  label,
  onClick,
}: ScheduleBlockProps) {
  const top = minuteToY(startMinute)
  const height = Math.max(2, minuteToY(endMinute) - top)

  if (kind === 'off') {
    return (
      <button
        type="button"
        onClick={onClick}
        onPointerDown={(e) => e.stopPropagation()}
        className={cn(
          'absolute left-1 right-1 z-10 flex items-center justify-center overflow-hidden rounded-md text-[11px] font-medium uppercase tracking-wide',
          'border border-dashed border-muted/40 bg-background-secondary/60 text-muted',
          'transition-[transform,box-shadow] duration-150 cursor-pointer',
          'hover:-translate-y-px hover:shadow-lg hover:z-20',
          'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
        )}
        style={{
          top,
          height,
          backgroundImage:
            'repeating-linear-gradient(135deg, rgba(255,255,255,0.04) 0 8px, transparent 8px 16px)',
        }}
      >
        {label}
      </button>
    )
  }

  if (kind === 'preview') {
    return (
      <div
        className="pointer-events-none absolute left-1 right-1 z-30 rounded-md border border-dashed border-primary bg-primary-light text-xs text-foreground"
        style={{ top, height }}
      >
        <div className="flex h-full flex-col justify-between p-1">
          <span className="text-[11px] font-medium tabular-nums">
            {formatMinute(startMinute)} — {formatMinute(endMinute)}
          </span>
        </div>
      </div>
    )
  }

  const isException = kind === 'exception'
  const bgAlpha = isException ? 0.4 : 0.2
  const borderAlpha = isException ? 0.9 : 0.6

  return (
    <button
      type="button"
      onClick={onClick}
      onPointerDown={(e) => e.stopPropagation()}
      className={cn(
        'absolute left-1 right-1 z-10 rounded-md text-left text-xs text-foreground',
        'transition-[transform,box-shadow,background-color] duration-150 outline-none cursor-pointer',
        'hover:-translate-y-px hover:shadow-lg hover:z-20',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
      )}
      style={{
        top,
        height,
        backgroundColor: hexToRgba(color, bgAlpha),
        borderLeft: `3px solid ${hexToRgba(color, borderAlpha)}`,
      }}
    >
      <div className="flex h-full flex-col gap-0.5 overflow-hidden p-1.5">
        {label && <span className="truncate text-[11px] font-medium">{label}</span>}
        <span className="text-[10px] tabular-nums text-muted">
          {formatMinute(startMinute)} — {formatMinute(endMinute)}
        </span>
      </div>
    </button>
  )
}
